import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { NotificationMethod } from '@treadinc/horizon-api-spec';
import { MessagePayload } from 'firebase/messaging';
import { t } from 'i18next';
import { useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import CustomToastContent from '~components/CustomToast/CustomToastContent';
import {
  MessagingChannel,
  MessagingMessage,
  principalId,
} from '~hooks/useMessaging/models';
import { isPageVisible } from '~hooks/useMessaging/pageVisibility';
import {
  MESSAGING_CHANNEL_QUERY_KEY,
  MESSAGING_CHANNELS_QUERY_KEY,
  MESSAGING_TARGET_CHANNEL_QUERY_KEY,
  messagingChannelQueryKey,
} from '~queries/messagingQueryKeys';
import { fetchMessagingChannel } from '~queries/useMessagingChannelQuery';
import type { MessagingChannelsPage } from '~queries/useMessagingChannelsInfiniteQuery';
import { ROUTES } from '~router/routePaths';
import { useStores } from '~store';
import { Nullable } from '~types/Nullable';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

// A chat toast left up as long as the generic 60 s push toast piles up during a busy shift.
export const MESSAGING_TOAST_AUTO_CLOSE_MS = 8000;

// A message's push and its realtime event land a few seconds apart.
export const MESSAGING_TOAST_DEDUPE_WINDOW_MS = 10_000;

// The api cuts the push excerpt at the same length.
const EXCERPT_LENGTH = 140;

// The api's `type` on a new-message push, which carries the conversation as `id`.
const MESSAGING_CHANNEL_PUSH_TYPE = 'messaging_channel';

type MessagingToastSource = 'push' | 'realtime';

const messagingToastId = (channelId: string) => `messaging:${channelId}`;

const excerptOf = (content: string) => {
  const firstLine = content.trim().split('\n')[0].trim();

  return firstLine.length > EXCERPT_LENGTH
    ? `${firstLine.slice(0, EXCERPT_LENGTH - 1)}…`
    : firstLine;
};

export default function useMessagingNewMessageToast({
  alertSound,
}: {
  alertSound: HTMLAudioElement;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { messagingOpenConversationStore, userStore } = useStores();
  const lastToastByChannelId = useRef(
    new Map<string, { source: MessagingToastSource; toastedAt: number }>(),
  );

  const findCachedChannel = useCallback(
    (channelId: string) => {
      const channelLists = queryClient.getQueriesData<
        InfiniteData<MessagingChannelsPage>
      >({ queryKey: MESSAGING_CHANNELS_QUERY_KEY });
      for (const [, channelList] of channelLists) {
        const listedChannel = channelList?.pages
          .flatMap((page) => page.channels)
          .find((channel) => channel.id === channelId);
        if (listedChannel) return listedChannel;
      }

      for (const queryKey of [
        MESSAGING_CHANNEL_QUERY_KEY,
        MESSAGING_TARGET_CHANNEL_QUERY_KEY,
      ]) {
        const cachedChannel = queryClient
          .getQueriesData<Nullable<MessagingChannel>>({ queryKey })
          .map(([, channel]) => channel)
          .find((channel) => channel?.id === channelId);
        if (cachedChannel) return cachedChannel;
      }

      return null;
    },
    [queryClient],
  );

  const isChannelMuted = useCallback(
    async (channelId: string) => {
      const cachedChannel = findCachedChannel(channelId);
      if (cachedChannel) return cachedChannel.muted;

      try {
        const channel = await queryClient.fetchQuery({
          queryKey: messagingChannelQueryKey(
            getEffectiveUserCompanyId(userStore),
            channelId,
          ),
          queryFn: ({ signal }) => fetchMessagingChannel(channelId, signal),
        });

        return channel.muted;
      } catch (error) {
        console.error('Failed to load the conversation to check its mute:', error);

        return false;
      }
    },
    [findCachedChannel, queryClient, userStore],
  );

  const showToast = useCallback(
    async ({
      body,
      channelId,
      source,
      title,
    }: {
      body: string;
      channelId: string;
      source: MessagingToastSource;
      title: string;
    }) => {
      const isReadingConversation =
        messagingOpenConversationStore.isOpen(channelId) && isPageVisible();
      const isNewMessageNotificationOn =
        userStore.user?.preferences?.notifications?.newMessage?.includes(
          NotificationMethod.PUSH,
        ) === true;
      if (isReadingConversation || !isNewMessageNotificationOn) return;
      if (await isChannelMuted(channelId)) return;

      const toastId = messagingToastId(channelId);
      const content = <CustomToastContent body={body} title={title} />;
      const isToastShowing = toast.isActive(toastId);

      // The push carries no message id, so a push and a realtime event for one conversation
      // inside the window are taken to be the same message.
      const lastToast = lastToastByChannelId.current.get(channelId);
      const isSameMessageAsLastToast =
        lastToast !== undefined &&
        lastToast.source !== source &&
        Date.now() - lastToast.toastedAt < MESSAGING_TOAST_DEDUPE_WINDOW_MS;
      const isRefreshingPushToast = source === 'realtime' && isToastShowing;
      if (isSameMessageAsLastToast && !isRefreshingPushToast) return;

      lastToastByChannelId.current.set(channelId, { source, toastedAt: Date.now() });

      if (isToastShowing) {
        toast.update(toastId, { render: content });

        return;
      }

      toast(content, {
        autoClose: MESSAGING_TOAST_AUTO_CLOSE_MS,
        closeOnClick: true,
        onClick: () => navigate(`/${ROUTES.messages}/${channelId}`),
        pauseOnHover: true,
        position: 'top-right',
        toastId,
      });
      alertSound.play().catch(console.error);
    },
    [alertSound, isChannelMuted, messagingOpenConversationStore, navigate, userStore],
  );

  const toastMessage = useCallback(
    (channelId: string, message: MessagingMessage) => {
      const isOwnMessage = principalId(message.sender) === userStore.user?.id;
      if (isOwnMessage || message.isDeleted) return;

      const excerpt = message.content ? excerptOf(message.content) : '';

      void showToast({
        body: excerpt || t('messaging.sent_an_attachment'),
        channelId,
        source: 'realtime',
        title: message.senderName ?? t('messaging.new_message'),
      });
    },
    [showToast, userStore],
  );

  const toastPush = useCallback(
    (payload: MessagePayload) => {
      const isMessagingPush = payload.data?.type === MESSAGING_CHANNEL_PUSH_TYPE;
      const channelId = isMessagingPush ? payload.data?.id : undefined;
      if (!channelId) return false;

      void showToast({
        body: payload.notification?.body ?? '',
        channelId,
        source: 'push',
        title: payload.notification?.title ?? '',
      });

      return true;
    },
    [showToast],
  );

  return { toastMessage, toastPush };
}
